import { useState, useEffect } from 'react';
import { Bell, BellOff, CheckCircle2, AlertTriangle, Loader2, Smartphone, ShieldCheck } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';

const API = import.meta.env.VITE_API_URL || '/api/v1';

const FIREBASE_CONFIG = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string,
};

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY as string;

async function getFCMToken(): Promise<string> {
  const { initializeApp, getApps } = await import('firebase/app');
  const { getMessaging, getToken } = await import('firebase/messaging');

  const app = getApps().length > 0 ? getApps()[0] : initializeApp(FIREBASE_CONFIG);
  const messaging = getMessaging(app);

  const token = await getToken(messaging, { vapidKey: VAPID_KEY });
  if (!token) throw new Error('Token FCM não obtido. Verifique a chave VAPID.');
  return token;
}

async function attachForegroundListener() {
  try {
    const { getMessaging, onMessage } = await import('firebase/messaging');
    const { getApps, initializeApp } = await import('firebase/app');
    const app = getApps().length > 0 ? getApps()[0] : initializeApp(FIREBASE_CONFIG);
    const messaging = getMessaging(app);
    onMessage(messaging, (payload) => {
      if (payload.notification && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        const n = new Notification(payload.notification.title || 'Aluno Conectado', {
          body: payload.notification.body || '',
          icon: '/favicon.png',
          data: payload.data,
        });
        n.onclick = () => {
          window.focus();
          const targetUrl = payload.data?.url || (payload.data?.tipoNotificacao === 'CHAT_MENSAGEM' || payload.data?.tipoNotificacao === 'CHAT' ? '/mobile/conversas' : '/mobile/comunicados');
          if (targetUrl) {
            window.location.href = targetUrl;
          }
        };
      }
    });
  } catch (err) {
    console.warn('[PwaPushEnforcer] Foreground listener could not be attached:', err);
  }
}

export function PwaPushEnforcer() {
  const { isAuthenticated, user } = useAuthStore();
  const [isStandalone, setIsStandalone] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [status, setStatus] = useState<'idle' | 'requesting' | 'registering' | 'success' | 'denied' | 'error'>('idle');
  const [error, setError] = useState('');

  useEffect(() => {
    // Detecta modo Standalone (PWA)
    const standaloneMode =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;
    setIsStandalone(standaloneMode);

    if (typeof Notification !== 'undefined') {
      setPermission(Notification.permission);
    }
  }, []);

  // Registro silencioso em segundo plano se a permissão já foi concedida
  useEffect(() => {
    if (isAuthenticated && user?.perfil === 'RESPONSAVEL' && isStandalone && permission === 'granted') {
      const registerSilently = async () => {
        try {
          const fcmToken = await getFCMToken();
          const jwtToken = localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token') || '';
          if (jwtToken) {
            const res = await fetch(`${API}/auth/push/register`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${jwtToken}`,
              },
              body: JSON.stringify({
                fcmToken,
                plataforma: 'WEB',
                tipo: 'pwa_silent',
              }),
            });
            if (!res.ok) {
              console.warn('[PwaPushEnforcer] Registro silencioso falhou:', res.statusText);
            } else {
              console.log('[PwaPushEnforcer] Registro silencioso concluído com sucesso');
            }
          }
          await attachForegroundListener();
        } catch (e) {
          console.error('[PwaPushEnforcer] Erro no registro silencioso:', e);
        }
      };
      registerSilently();
    }
  }, [isAuthenticated, user, isStandalone, permission]);

  // Só se aplica a usuários autenticados do tipo RESPONSAVEL em modo Standalone
  const needsEnforcement =
    isAuthenticated &&
    user?.perfil === 'RESPONSAVEL' &&
    isStandalone &&
    permission !== 'granted';

  if (!needsEnforcement) {
    return null;
  }

  const handleRequestPermission = async () => {
    if (typeof Notification === 'undefined') {
      setError('Notificações não são suportadas neste dispositivo/navegador.');
      setStatus('error');
      return;
    }

    setStatus('requesting');
    setError('');

    try {
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result !== 'granted') {
        setStatus('denied');
        return;
      }

      setStatus('registering');
      const fcmToken = await getFCMToken();

      // Envia o token FCM para o backend
      const jwtToken = localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token') || '';
      if (jwtToken) {
        const res = await fetch(`${API}/auth/push/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwtToken}`,
          },
          body: JSON.stringify({
            fcmToken,
            plataforma: 'WEB',
            tipo: 'pwa_mandatory',
          }),
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.message || 'Falha ao registrar token push no servidor');
        }
      }

      // Ativar foreground listener
      await attachForegroundListener();

      setStatus('success');
      // Pequeno delay para exibir animação de sucesso antes de sumir
      setTimeout(() => {
        setPermission('granted');
      }, 1500);
    } catch (e: any) {
      console.error('[PWA Push Enforcer] Erro ao registrar:', e);
      setError(e.message || 'Erro ao registrar as notificações push.');
      setStatus('error');
    }
  };

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document);

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 99999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(-45deg, #0a0518, #11082d, #061122, #0d091e)',
      backgroundSize: '400% 400%',
      animation: 'pan-bg 15s ease infinite',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      padding: 16,
    }}>
      <div style={{
        background: '#ffffff',
        borderRadius: 24,
        padding: '30px 24px',
        maxWidth: 420,
        width: '100%',
        boxShadow: '0 25px 60px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05)',
        textAlign: 'center',
        animation: 'fade-in-up 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
      }}>
        {status === 'success' ? (
          <>
            <div style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              margin: '0 auto 20px',
              background: '#f0fdf4',
              border: '2px solid #bbf7d0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 20px rgba(22,163,74,0.08)',
            }}>
              <CheckCircle2 size={30} style={{ color: '#16a34a' }} />
            </div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#16a34a', margin: '0 0 8px', letterSpacing: '-0.02em' }}>
              Notificações Ativadas!
            </h2>
            <p style={{ color: '#64748b', fontSize: '.84rem', lineHeight: 1.6, margin: 0 }}>
              Parabéns! Suas notificações push foram habilitadas com sucesso. Agora você está pronto para acessar o painel do seu filho.
            </p>
          </>
        ) : status === 'denied' || permission === 'denied' ? (
          <>
            <div style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              margin: '0 auto 20px',
              background: '#fef2f2',
              border: '2px solid #fecaca',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 20px rgba(239,68,68,0.08)',
            }}>
              <BellOff size={30} style={{ color: '#ef4444' }} />
            </div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1e293b', margin: '0 0 8px', letterSpacing: '-0.02em' }}>
              Notificações Bloqueadas
            </h2>
            <p style={{ color: '#64748b', fontSize: '.84rem', lineHeight: 1.5, marginBottom: 18 }}>
              Por segurança, o acesso é restrito até que as notificações sejam permitidas nas configurações do seu celular.
            </p>

            <div style={{
              textAlign: 'left',
              background: '#f8fafc',
              borderRadius: 14,
              padding: '14px 16px',
              border: '1px solid #e2e8f0',
              marginBottom: 18,
            }}>
              <p style={{ fontSize: '.76rem', fontWeight: 700, color: '#475569', margin: '0 0 8px', textTransform: 'uppercase' }}>
                Como reativar no celular:
              </p>
              {isIOS ? (
                <ol style={{ fontSize: '.8rem', color: '#64748b', margin: 0, paddingLeft: 16, lineHeight: 1.6 }}>
                  <li>Saia do app e vá nos <strong>Ajustes</strong> do iPhone.</li>
                  <li>Selecione <strong>Notificações</strong>.</li>
                  <li>Busque por <strong>Aluno Conectado</strong> na lista.</li>
                  <li>Ative a opção <strong>Permitir Notificações</strong>.</li>
                  <li>Abra o aplicativo novamente.</li>
                </ol>
              ) : (
                <ol style={{ fontSize: '.8rem', color: '#64748b', margin: 0, paddingLeft: 16, lineHeight: 1.6 }}>
                  <li>Abra as <strong>Configurações</strong> do celular (Engrenagem).</li>
                  <li>Vá em <strong>Apps / Aplicativos</strong> e procure por <strong>Aluno Conectado</strong>.</li>
                  <li>Selecione <strong>Notificações</strong>.</li>
                  <li>Ative a chave <strong>Permitir Notificações</strong>.</li>
                  <li>Abra o aplicativo novamente.</li>
                </ol>
              )}
            </div>

            <button
              onClick={handleRequestPermission}
              style={{
                width: '100%',
                padding: '14px 20px',
                borderRadius: 14,
                border: '1.5px solid #e2e8f0',
                background: '#f8fafc',
                color: '#475569',
                fontSize: '.88rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              Já ativei nas Configurações
            </button>
          </>
        ) : (
          <>
            <div style={{ position: 'relative', display: 'inline-block', marginBottom: 20 }}>
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(108,92,231,0.08) 0%, transparent 70%)',
                pointerEvents: 'none',
              }} />
              <div style={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                position: 'relative',
                background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)',
                border: '1px solid #ddd6fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 20px rgba(108,92,231,0.06)',
              }}>
                <Bell size={28} strokeWidth={1.5} style={{ color: '#6c5ce7', animation: 'pulse 2s infinite' }} />
              </div>
            </div>

            <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1e293b', margin: '0 0 8px', letterSpacing: '-0.02em' }}>
              Ativar Notificações do App
            </h2>
            <p style={{ color: '#475569', fontSize: '.84rem', lineHeight: 1.5, marginBottom: 20 }}>
              Olá! Para sua segurança e para receber atualizações, avisos e eventos escolares do seu filho em tempo real, <strong>é obrigatório</strong> habilitar as notificações do Aluno Conectado no seu celular.
            </p>

            {error && (
              <div style={{
                padding: '10px 14px',
                borderRadius: 10,
                background: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#dc2626',
                fontSize: '.78rem',
                marginBottom: 16,
                textAlign: 'left',
              }}>
                {error}
              </div>
            )}

            <button
              onClick={handleRequestPermission}
              disabled={status === 'requesting' || status === 'registering'}
              style={{
                width: '100%',
                padding: '16px 24px',
                borderRadius: 16,
                border: 'none',
                background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                color: '#ffffff',
                fontSize: '1rem',
                fontWeight: 800,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                boxShadow: '0 6px 24px rgba(108,92,231,0.35)',
                transition: 'all 0.2s',
              }}
            >
              {status === 'requesting' || status === 'registering' ? (
                <>
                  <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  Habilitando...
                </>
              ) : (
                <>
                  <Bell size={18} />
                  Ativar Notificações Push
                </>
              )}
            </button>
          </>
        )}

        <div style={{
          marginTop: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
        }}>
          <ShieldCheck size={14} style={{ color: '#94a3b8' }} />
          <span style={{ fontSize: '.76rem', color: '#94a3b8', fontWeight: 500 }}>
            Conexão Criptografada . Escola Segura
          </span>
        </div>
      </div>
      <style>{`
        @keyframes pan-bg {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
